import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";

const freightCodeType = z.enum(["ship_via", "freight_terms", "credit_terms"]);
const compilesCaseInsensitive = (pattern: string): boolean => {
  try {
    new RegExp(pattern, "i");
    return true;
  } catch {
    return false;
  }
};

const freightTermsKind = z.enum([
  "prepaid",
  "prepaid_and_charge",
  "collect",
  "other",
]);

export const freightCodeInputSchema = z.object({
  dataSourceLink: z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid().optional(),
  }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),
  divisionCode: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Division the code belongs to. When blank, the link's DataSource title is used.",
    ),
  codeType: z
    .string()
    .nullable()
    .describe("The source's code family, mapped through typeMapping."),
  code: z.string().nullable(),
  description: z.string().nullable().optional(),
});
export type FreightCodeInput = z.infer<typeof freightCodeInputSchema>;

export const upsertFreightCodeStep = defineStepConfig({
  type: "upsert_freight_code",
  name: "Upsert Freight Code",
  description:
    "Maintains a division's carrier, freight-terms and credit-terms codes, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: freightCodeInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "unmapped_type",
        "missing_division",
        "missing_code",
        "stale_event",
        "not_found",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    writeOriginSystem: z.string().min(1),
    typeMapping: z
      .record(z.string(), freightCodeType)
      .prefault({
        SHIP_VIA: "ship_via",
        FREIGHT_TERMS: "freight_terms",
        CREDIT_TERMS: "credit_terms",
      })
      .describe(
        "Source code families mapped to freight code types. Unmapped families are retired.",
      ),
    // Rejected when the config is parsed, so a bad pattern cannot fail every
    // freight-terms row mid-ingest.
    termsKindRules: z
      .array(
        z.object({
          kind: freightTermsKind,
          pattern: z
            .string()
            .min(1)
            .refine(compilesCaseInsensitive, {
              message: "Must be a valid regular expression.",
            })
            .describe(
              "Case-insensitive regular expression tested against the description.",
            ),
        }),
      )
      .prefault([
        { kind: "prepaid_and_charge", pattern: "(PREPAID|PPD|P\\.PD).*CHARGE" },
        { kind: "collect", pattern: "\\bCOLL" },
        { kind: "prepaid", pattern: "PREPAID|\\bPPD\\b|P\\.PD" },
      ])
      .describe(
        "Ordered rules that classify a freight-terms description; the first match wins and no match means `other`.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
